/**
 * On an evaluation copy: the live case's partner returns, to take onto the
 * copy under one of its assignments (05 §1, decision 6). Each is placed as
 * the callback places a real one, and the case agent's arrival run checks
 * it. No file names: they may carry the client's reference.
 */
import { useEffect, useState } from 'react';

import { Badge, Button, Card, Group, Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { formatWhen } from '@/components/Playbooks/format';
import { vnextApiClient } from '@/services/vnext-api';
import type {
  WorkbenchReturnAssignment,
  WorkbenchSourceReturn,
  WorkbenchSourceReturns,
} from '@/types/workbench';
import { toError } from '@/utils/errors';

interface SourceReturnsPanelProps {
  reportId: string;
  /** The case taken from, as the header names it. */
  sourceCodename: string | null;
  onTaken: () => void | Promise<void>;
}

function assignmentLabel(a: WorkbenchReturnAssignment): string {
  const where = a.jurisdictions.length > 0 ? a.jurisdictions.join(', ') : 'anywhere';
  const what = a.components.length > 0 ? a.components.join(', ') : 'no components';
  return `${where}: ${what}${a.open ? '' : ' (cancelled)'}`;
}

function returnLabel(r: WorkbenchSourceReturn): string {
  const when = r.receivedAt ? formatWhen(r.receivedAt) : 'undated';
  const request = r.npRequestId != null ? `request ${r.npRequestId}` : 'a request';
  const pictures = r.pictures > 0 ? ` · ${r.pictures} picture${r.pictures === 1 ? '' : 's'}` : '';
  return `Return on ${request}, ${when}${pictures}`;
}

export function SourceReturnsPanel({
  reportId,
  sourceCodename,
  onTaken,
}: Readonly<SourceReturnsPanelProps>) {
  const [found, setFound] = useState<WorkbenchSourceReturns | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Record<string, string | null>>({});
  const [taking, setTaking] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await vnextApiClient.listSourceReturns(reportId);
        if (!cancelled) {
          setFound(result);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(toError(err, "Could not read the live case's returns").message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  if (error) {
    return (
      <Text size="sm" c="dimmed" data-testid="source-returns-error">
        {error}
      </Text>
    );
  }
  if (!found) {
    return null;
  }
  const options = found.assignments.map((a) => ({ value: a.id, label: assignmentLabel(a) }));
  const take = async (r: WorkbenchSourceReturn) => {
    const assignmentId = chosen[r.reportDataId] ?? r.suggestedAssignmentId;
    if (!assignmentId) {
      return;
    }
    setTaking(r.reportDataId);
    try {
      const taken = await vnextApiClient.takeSourceReturn(reportId, {
        reportDataId: r.reportDataId,
        assignmentId,
      });
      notifications.show({
        color: 'teal',
        message: `Taken — ${taken.files} file${taken.files === 1 ? '' : 's'} placed on the copy; the case agent checks it next.`,
      });
      await onTaken();
    } catch (err) {
      Sentry.captureException(toError(err));
      notifications.show({ color: 'red', message: toError(err, 'Could not take it').message });
    } finally {
      setTaking(null);
    }
  };

  return (
    <Card withBorder padding="sm" radius="md" data-testid="source-returns">
      <Stack gap="xs">
        <Text size="sm" fw={600}>
          The live case&apos;s returns{sourceCodename ? ` (${sourceCodename})` : ''}
        </Text>
        {found.returns.length === 0 ? (
          <Text size="sm" c="dimmed">
            None yet: a partner&apos;s return on the live case appears here to take.
          </Text>
        ) : null}
        {found.returns.map((r) => {
          const value = chosen[r.reportDataId] ?? r.suggestedAssignmentId;
          return (
            <Group
              key={r.reportDataId}
              justify="space-between"
              wrap="wrap"
              gap="xs"
              data-testid={`source-return-${r.reportDataId}`}
            >
              <Group gap={6} wrap="nowrap">
                <Text size="sm">{returnLabel(r)}</Text>
                {r.superseded ? (
                  <Badge size="xs" variant="light" color="gray" style={{ textTransform: 'none' }}>
                    Superseded
                  </Badge>
                ) : null}
              </Group>
              <Group gap={6} wrap="nowrap">
                <Select
                  size="xs"
                  placeholder="Under which assignment"
                  data={options}
                  value={value}
                  onChange={(id) => setChosen((prev) => ({ ...prev, [r.reportDataId]: id }))}
                  w={280}
                  data-testid={`source-return-assignment-${r.reportDataId}`}
                />
                <Button
                  size="compact-xs"
                  disabled={!value}
                  loading={taking === r.reportDataId}
                  onClick={() => void take(r)}
                  data-testid={`take-return-${r.reportDataId}`}
                >
                  Take
                </Button>
              </Group>
            </Group>
          );
        })}
      </Stack>
    </Card>
  );
}
